import React from 'react';
import { PropTypes } from 'prop-types';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks';
import styled, { css } from 'styled-components';
import { lightenDarkenColor } from '../utils/styles';

const FontSizer = observer(
  ({
    showPlusMinus,
    storagePropName,
    storedValue,
    boundary,
    plusMinusLabel,
  }) => {
    const {
      UIStore: { handleStorageFontSize },
    } = useStores();

    const fontIncConfig = [
      { icon: 'fa-plus', value: 1 },
      { icon: 'fa-minus', value: -1 },
    ];

    // size here is added to base font size (ie. base font of 1em + 3 = 1.3em, not 4em)
    const fontLetterConfig = [
      {
        buttonSize: '.5rem',
        size: 0,
      },
      {
        buttonSize: '.75rem',
        size: 3,
      },
      {
        buttonSize: '1rem',
        size: 6,
      },
      {
        buttonSize: '1.2rem',
        size: 9,
      },
    ];

    const incrementDecrementSetting = (value) => ({
      value,
      prop: storagePropName,
      type: 'incDecValue',
      boundary,
    });

    const setValueSetting = (value) => ({
      value,
      prop: storagePropName,
      type: 'setValue',
    });

    return showPlusMinus ? (
      <PlusMinusContainer>
        <Text>{plusMinusLabel}</Text>
        {fontIncConfig.map((item) => (
          <PlusMinusButton
            key={`plusMinusBtn_valueIs_${item.value}`}
            onClick={() =>
              handleStorageFontSize(incrementDecrementSetting(item.value))
            }
          >
            <ButtonIcon iconName={item.icon} />
          </PlusMinusButton>
        ))}
      </PlusMinusContainer>
    ) : (
      <LetterButtonContainer>
        {fontLetterConfig.map((item) => (
          <LetterButton
            key={`letterBtn_sizeIs_${item.size}`}
            isActive={storedValue === item.size}
            buttonTextSize={item.buttonSize}
            onClick={() => handleStorageFontSize(setValueSetting(item.size))}
          >
            <LetterIcon />
          </LetterButton>
        ))}
      </LetterButtonContainer>
    );
  },
);

FontSizer.propTypes = {
  showPlusMinus: PropTypes.bool,
  storagePropName: PropTypes.string.isRequired,
  storedValue: PropTypes.number,
  boundary: PropTypes.array,
  plusMinusLabel: PropTypes.string,
};

FontSizer.defaultProps = {
  showPlusMinus: false,
  storedValue: 0,
  boundary: [-3, 9],
  plusMinusLabel: 'Font:',
};

export default FontSizer;

const ButtonIcon = ({ iconName }) => (
  <i className={`fa ${iconName}`} aria-hidden="true" />
);
const LetterIcon = () => <ButtonIcon iconName="fa-font" />;

const LetterButton = styled.button`
  ${({ buttonTextSize, isActive }) => css`
    color: ${isActive ? '#fff' : '#000'};
    background-color: ${isActive ? '#0095E8' : '#e7e7e7'};
    font-size: ${buttonTextSize};
    outline: none;
    border: 1px solid #000;
    padding: 1px 4px;
    margin-right: 3px;
    &:hover {
      background-color: ${lightenDarkenColor(
        isActive ? '#0095E8' : '#e7e7e7',
        20,
      )};
    }
  `}
`;

const LetterButtonContainer = styled.div`
  display: flex;
  width: 100px;
  margin-bottom: -8px;
`;

const PlusMinusButton = styled.button`
  color: #000;
  padding: 0;
  margin: 0 0 0 5px;
  outline: none;
`;

const PlusMinusContainer = styled.div`
  display: flex;
  align-items: flex-end;
  /* margin: 5px 0 -5px 5px; */
`;

const Text = styled.span``;
